export type OpenAlexRef = {
  id?: string
  title?: string
  year?: number
  abstract_preview?: string
}

export function parseSourceRefs(raw: unknown): OpenAlexRef[] {
  if (typeof raw !== 'string' || !raw.trim()) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter((x): x is OpenAlexRef => x != null && typeof x === 'object')
  } catch {
    return []
  }
}

export function openAlexHref(id: string): string {
  const t = id.trim()
  if (t.startsWith('http')) return t
  const w = t.startsWith('W') ? t : t.replace(/^.*\//, '')
  return `https://openalex.org/${w}`
}

type Props = { sourceRefs: unknown }

/** OpenAlex / research basis block for admin preview */
export function NudgeModalResearchSection({ sourceRefs }: Props) {
  const refs = parseSourceRefs(sourceRefs)
  if (!refs.length) return null
  return (
    <div className="mt-4 pt-4 border-t border-warm-border/70 space-y-1.5">
      <p className="text-[10px] font-semibold text-ink-muted uppercase tracking-wide">Research basis (OpenAlex batch)</p>
      <ul className="space-y-3 list-none">
        {refs.map((r, i) => {
          const idStr = r.id != null ? String(r.id) : ''
          const href = idStr ? openAlexHref(idStr) : null
          const preview = typeof r.abstract_preview === 'string' ? r.abstract_preview.trim() : ''
          return (
            <li key={`${idStr}-${i}`} className="text-[10px] text-ink leading-snug space-y-1">
              <div>
                {r.year != null ? <span className="text-ink-muted">{r.year}. </span> : null}
                {r.title != null ? String(r.title) : 'Untitled'}
                {href ? (
                  <>
                    {' '}
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-teal underline font-medium"
                    >
                      OpenAlex
                    </a>
                  </>
                ) : null}
              </div>
              {preview ? (
                <p className="text-[10px] text-ink-muted whitespace-pre-wrap pl-2 border-l-2 border-warm-border leading-relaxed">
                  {preview}
                  {preview.length >= 1_200 ? '…' : ''}
                </p>
              ) : (
                <p className="text-[9px] text-ink-muted italic pl-2 border-l-2 border-warm-border/50">
                  No abstract returned by OpenAlex for this work.
                </p>
              )}
            </li>
          )
        })}
      </ul>
      <p className="text-[9px] text-ink-muted">
        Abstracts are saved as previews (up to ~1,200 characters per paper) from OpenAlex. Older pending rows may only
        show titles until the next monthly ingest.
      </p>
    </div>
  )
}
